import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, School, PenLine, Save, MessageCircleHeart } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";

const ICONS = [Sparkles, School, PenLine, Save, MessageCircleHeart];

export default function Onboarding() {
  const { t } = useLang();
  const { refresh } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const steps = t("onboarding.steps");

  const finish = async () => {
    try { await api.post("/auth/complete-onboarding"); } catch {}
    await refresh();
    navigate("/dashboard");
  };

  const Icon = ICONS[step];
  const isLast = step === steps.length - 1;

  return (
    <div className="min-h-screen bg-primary flex items-center justify-center p-6">
      <div className="w-full max-w-lg bg-card rounded-3xl border border-border p-8 sm:p-12 space-y-8">
        <div className="flex justify-between items-center">
          <div className="flex gap-1.5">
            {steps.map((_, i) => (
              <span key={i} className={`h-2 rounded-full transition-all ${i === step ? "w-8 bg-secondary" : "w-2 bg-muted"}`} />
            ))}
          </div>
          <button onClick={finish} className="text-sm text-muted-foreground hover:text-foreground" data-testid="onboarding-skip">{t("onboarding.skip")}</button>
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.3 }}
            className="space-y-4 text-center py-6"
          >
            <span className="mx-auto h-16 w-16 rounded-2xl bg-secondary/15 flex items-center justify-center">
              <Icon className="h-8 w-8 text-secondary" />
            </span>
            <h1 className="font-heading font-extrabold text-3xl tracking-tight">{steps[step].title}</h1>
            <p className="text-muted-foreground text-lg leading-relaxed">{steps[step].body}</p>
          </motion.div>
        </AnimatePresence>

        <div className="flex gap-3">
          {step > 0 && (
            <Button variant="outline" className="flex-1 h-12 rounded-xl" onClick={() => setStep((s) => s - 1)} data-testid="onboarding-back">
              {t("onboarding.back")}
            </Button>
          )}
          <Button
            className="flex-1 h-12 rounded-xl bg-secondary hover:bg-secondary/90"
            onClick={() => (isLast ? finish() : setStep((s) => s + 1))}
            data-testid="onboarding-next"
          >
            {isLast ? t("onboarding.finish") : t("onboarding.next")}
          </Button>
        </div>
      </div>
    </div>
  );
}
